import { merge, toArray } from 'lodash';
import set from 'lodash/set';
import { DateTime } from 'luxon';
import { proxy } from 'valtio';
import {
  defaultBasemapStyle,
  EditorMode,
  LoadingState,
  type MapDetail,
  type MapObject,
  type Project,
  type TLoadedState,
  type TLoadingState,
  type UnitSystem,
} from '~/common/types';
import { MapStoreBase } from '~/components2/Map';

const defaultMap: TLoadedState & MapDetail = {
  isLoading: false,
  id: 'temp',
  name: 'temp',
  image: 'temp',
  unitSystem: 'imperial',
  baseMap: defaultBasemapStyle,
  createdAt: DateTime.now(),
  createdBy: {
    name: 'temp',
  },
  updatedAt: DateTime.now(),
  updatedBy: {
    name: 'temp',
  },
  objects: {},
};

class ProjectDetailsMapStore extends MapStoreBase {
  inEditGeometryMode: boolean = false;
  project: TLoadingState | (TLoadedState & Project) = LoadingState;
  projectId: string = '';
  editorMode: EditorMode = EditorMode.None;
  currentMap: TLoadingState | (TLoadedState & MapDetail) = {
    ...LoadingState,
  };
  proposalGridSize: number = 30;

  get currentObject() {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      return this.currentMap.objects[this.currentObjectId];
    }
    return undefined;
  }

  get objectsAsArray(): MapObject[] {
    if (!this.currentMap.isLoading) {
      return toArray(this.currentMap.objects);
    } else {
      return [];
    }
  }

  get objectsOnMainSurface() {
    if (!this.currentMap.isLoading) {
      if (this.currentObjectId) {
        const copy = { ...this.currentMap.objects };
        delete copy[this.currentObjectId];

        return copy;
      } else {
        return this.currentMap.objects;
      }
    } else {
      return {};
    }
  }

  get currentUnitSystem(): UnitSystem {
    return 'imperial';
  }

  setEditorMode(mode: EditorMode): void {
    this.editorMode = mode;
  }

  generateSortOrderKey(): string {
    return 'a1';
  }

  setCurrentObjectId(objectId: string): void {
    if (!this.currentMap.isLoading) {
      const object = this.currentMap.objects[objectId];
      if (object) {
        this.currentObjectId = object.id;
        if (object.type === 'polygon') {
          this.setEditorMode(EditorMode.EditPolygon);
        } else if (object.type === 'line') {
          this.setEditorMode(EditorMode.EditLine);
        }
      }
    }
  }

  setProposalGridSize(size: number): void {
    this.proposalGridSize = size;
  }

  async resetCurrentObjectId(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.currentObjectId = undefined;
        this.setEditorMode(EditorMode.None);
        resolve();
      }, 0);
    });
  }

  async newObject(o: Omit<MapObject, 'createdBy' | 'updatedBy'>): Promise<MapObject | undefined> {
    set(this.currentMap, `objects.${o.id}`, o);
    return undefined;
  }

  async updateObject<T = MapObject>(o: Partial<T>): Promise<void> {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      const merged = merge(this.currentObject, o);
      this.currentMap.objects[this.currentObjectId!] = merged;
    }
  }

  async deleteObject<T = MapObject>(o: T, shouldPushToUndoStack?: boolean): Promise<MapObject | undefined> {
    if (!this.currentMap.isLoading) {
      delete this.currentMap.objects[(o as MapObject).id];
      this.currentObjectId = undefined;
    }

    return undefined;
  }

  setup() {
    this.currentMap = JSON.parse(JSON.stringify(defaultMap));
  }

  reset() {
    this.currentMap = { isLoading: true };
    this.currentObjectId = undefined;
    this.editorMode = EditorMode.None;
    this.hiddenObjects = [];
  }
}

const projectDetailsMapStore = proxy(new ProjectDetailsMapStore());
export default projectDetailsMapStore;
